import * as React from 'react';
import { useState } from 'react';
import styled from '@emotion/styled';
import ActionsCruise from '../ActionsCruisePage/ActionsCruise';
import { __ } from '../../helpers/TranslationService';
export interface ICruisePageTabs {
    icon: () => JSX.Element;
    title: string;
    content: () => JSX.Element | string;
    isActive: boolean;
    trackingFunction?: () => void;
}

interface IProps {
    tabs: ICruisePageTabs[];
    cruiseNid: number;
    activeTabIndex?: number;
}

const TabsWrapper = styled.div`
    display: flex;
    overflow: hidden;
    flex-direction: column;
`;

const TabsNav = styled.nav`
    display: flex;
    align-items: flex-end;
    width: 100%;
    float: left;
    padding-left: 0;
    padding-right: 0;
    justify-content: space-between;
`;

const TabsNavList = styled.ul`
    display: flex;
    flex-direction: row;
    margin-top: 26px;
    margin-bottom: 0;
`;

const TabsNavListItemTitle = styled.div`
    font-size: 1.166rem;
    color: ${({ theme }) => theme.productPage.tabs.textColor};
    display: flex;
    box-sizing: border-box;
    align-items: center;
    padding: 8px 16px;
    transition: color 0.6s ease-out;
`;

const TabsNavListItem = styled.li`
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: center;
    border: 2px solid ${({ theme }) => theme.productPage.tabs.borderColor};
    border-width: 1px 1px 0;
    background-color: ${({ theme }) => theme.productPage.tabs.backgroundColor};
    cursor: pointer;
    border-radius: 6px 6px 0 0;
    transition-property: color, background-color, border;
    transition-duration: 0.4s, 0.5s, 0.5s;
    transition-timing-function: ease-out;

    &.active,
    &:hover,
    &:focus {
        background-color: ${({ theme }) => theme.productPage.tabs.backgroundColorActive};
        border-color: ${({ theme }) => theme.productPage.tabs.borderColorActive};
        & > div {
            color: ${({ theme }) => theme.productPage.tabs.textColorActive};
            svg {
                fill: ${({ theme }) => theme.productPage.tabs.iconColorActive};
            }
        }
    }

    &.active {
        cursor: default;
    }

    &:not(:first-of-type) {
        margin-left: 6px;
    }

    & svg {
        margin-right: 5px;
        transition: fill 0.5s ease-out;
        max-width: 18px;
        max-height: 18px;
        fill: ${({ theme }) => theme.productPage.tabs.iconColor};
    }
`;

const TabContent = styled.div`
    display: none;
    height: 0;
    overflow: visible;
    padding: 24px;
    box-sizing: border-box;
    &.active {
        display: inline-block;
        height: auto;
        background-color: ${({ theme }) => theme.colors.white[100]};
        border: 2px solid ${({ theme }) => theme.colors.seashell[100]};
        transition: opacity 0.4s ease-out;
    }
`;

const CruisePageTabs = React.forwardRef(function CruisePageTabs(
    { tabs, cruiseNid, activeTabIndex }: IProps,
    ref: React.RefObject<HTMLDivElement>,
) {
    const routeTabIndex = tabs.findIndex((tab) => tab.title === __('Itinerary', 'dreamlines'));

    const [activeIndex, setActiveIndex] = useState(activeTabIndex || 0);
    const [renderedTabs, setRenderedTabs] = useState([routeTabIndex, activeTabIndex || 0]);

    React.useEffect(() => {
        if (activeTabIndex !== undefined) {
            setActiveIndex(activeTabIndex);
            setRenderedTabs([activeTabIndex]);
        }
    }, [activeTabIndex]);

    if (!tabs) {
        return null;
    }

    const onlyForRenderTabs = tabs.filter((i) => i.isActive);

    const handleClick = (index) => {
        setActiveIndex(index);
        if (!renderedTabs.includes(index)) {
            setRenderedTabs((prevState) => [...prevState, index]);
        }
        if (onlyForRenderTabs[index].trackingFunction) {
            onlyForRenderTabs[index].trackingFunction();
        }
    };

    return (
        <TabsWrapper ref={ref}>
            <TabsNav>
                <TabsNavList data-testid="tab-list">
                    {onlyForRenderTabs.map(
                        (tab, index) =>
                            tab.isActive && (
                                <TabsNavListItem
                                    data-testid={'tab'}
                                    className={activeIndex === index ? 'active' : ''}
                                    key={index}
                                    onClick={() => handleClick(index)}
                                >
                                    <TabsNavListItemTitle>
                                        {tab.icon()}
                                        {tab.title}
                                    </TabsNavListItemTitle>
                                </TabsNavListItem>
                            ),
                    )}
                </TabsNavList>

                <ActionsCruise isShareVisible={true} cruiseNid={cruiseNid} />
            </TabsNav>
            {onlyForRenderTabs.map(
                (tab, index) =>
                    tab.isActive && (
                        <TabContent
                            data-testid="tab-content"
                            className={activeIndex === index ? 'active' : ''}
                            key={index}
                        >
                            {renderedTabs.includes(index) && onlyForRenderTabs[index].content()}
                        </TabContent>
                    ),
            )}
        </TabsWrapper>
    );
});

export { CruisePageTabs };
